import { useCallback, useEffect, useMemo, useState } from 'react'

import type { ExtendedColumnDef, ResponsiveConfig, ScreenSize } from '../DataTable.types'
import { debounce, filterVisibleColumns, getScreenSize } from '../DataTable.utils'

// Hook for responsive table behavior
export const useResponsive = <TData>(
  columns: ExtendedColumnDef<TData>[],
  config?: ResponsiveConfig
) => {
  const [screenSize, setScreenSize] = useState<ScreenSize>(() => getScreenSize())

  // Update screen size on window resize
  useEffect(() => {
    const handleResize = debounce(() => {
      setScreenSize(getScreenSize())
    }, 150)

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Filter columns based on current screen size
  const visibleColumns = useMemo(() => {
    return filterVisibleColumns(columns, screenSize, config)
  }, [columns, screenSize, config])

  // Get responsive info
  const responsiveInfo = useMemo(() => {
    const hiddenColumns = columns.filter(
      (col) => !visibleColumns.find((visible) => visible.id === col.id)
    )

    return {
      screenSize,
      totalColumns: columns.length,
      visibleColumns: visibleColumns.length,
      hiddenColumns: hiddenColumns.length,
      hiddenColumnIds: hiddenColumns.map((col) => col.id).filter(Boolean) as string[],
      isMobile: screenSize === 'mobile',
      isTablet: screenSize === 'tablet',
      isDesktop: screenSize === 'desktop'
    }
  }, [columns, visibleColumns, screenSize])

  return {
    visibleColumns,
    responsiveInfo,
    screenSize
  }
}

// Hook for managing breakpoints
export const useBreakpoints = (customBreakpoints?: {
  mobile?: number
  tablet?: number
  desktop?: number
}) => {
  // const breakpoints = { mobile: 768, tablet: 1024, desktop: 1280, ...customBreakpoints }
  const breakpoints = useMemo(
    () => ({ mobile: 768, tablet: 1024, desktop: 1280, ...customBreakpoints }),
    [customBreakpoints]
  )

  const [windowWidth, setWindowWidth] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth
    }
    return 1024 // Default to tablet size for SSR
  })

  useEffect(() => {
    const handleResize = debounce(() => {
      setWindowWidth(window.innerWidth)
    }, 100)

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const isMobile = windowWidth < breakpoints.mobile
  const isTablet = windowWidth >= breakpoints.mobile && windowWidth < breakpoints.tablet
  const isDesktop = windowWidth >= breakpoints.tablet

  const currentBreakpoint: ScreenSize = isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop'

  const isAbove = useCallback(
    (breakpoint: keyof typeof breakpoints) => {
      return windowWidth >= breakpoints[breakpoint]
    },
    [windowWidth, breakpoints]
  )

  const isBelow = useCallback(
    (breakpoint: keyof typeof breakpoints) => {
      return windowWidth < breakpoints[breakpoint]
    },
    [windowWidth, breakpoints]
  )

  return {
    windowWidth,
    breakpoints,
    isMobile,
    isTablet,
    isDesktop,
    currentBreakpoint,
    isAbove,
    isBelow
  }
}

// Hook for managing responsive table layout
export const useResponsiveLayout = (screenSize: ScreenSize, enableStacking?: boolean) => {
  const shouldStack = enableStacking && screenSize === 'mobile'
  const shouldCompact = screenSize === 'mobile' || screenSize === 'tablet'

  const getLayoutClasses = useCallback(() => {
    return {
      table: shouldStack ? 'block' : 'table',
      thead: shouldStack ? 'hidden' : 'table-header-group',
      tbody: shouldStack ? 'block' : 'table-row-group',
      tr: shouldStack ? 'block border-b pb-4 mb-4' : 'table-row',
      th: shouldStack ? 'hidden' : 'table-cell',
      td: shouldStack ? 'block' : 'table-cell'
    }
  }, [shouldStack])

  const getCellPadding = useCallback(() => {
    if (shouldCompact) return 'p-2'
    return 'p-4'
  }, [shouldCompact])

  return {
    shouldStack,
    shouldCompact,
    getLayoutClasses,
    getCellPadding
  }
}

// Hook for adaptive column sizing
export const useAdaptiveColumns = <TData>(
  columns: ExtendedColumnDef<TData>[],
  containerWidth: number
) => {
  const adaptedColumns = useMemo(() => {
    // Calculate available width per column
    const availableWidth = containerWidth
    const columnCount = columns.length
    const averageWidth = availableWidth / columnCount

    return columns.map((column) => {
      const meta = column.meta || {}

      // Use fixed width if specified
      if (meta.width) {
        return column
      }

      // Calculate dynamic width based on priority and content
      const priority = meta.priority || 5
      const priorityMultiplier = (11 - priority) / 10 // Higher priority = more width
      const dynamicWidth = Math.max(80, averageWidth * priorityMultiplier)

      return {
        ...column,
        meta: {
          ...meta,
          width: `${Math.round(dynamicWidth)}px`
        }
      }
    })
  }, [columns, containerWidth])

  return adaptedColumns
}

// Hook for handling responsive table actions
export const useResponsiveActions = (screenSize: ScreenSize) => {
  const shouldCollapse = screenSize === 'mobile'
  const shouldGroup = screenSize === 'mobile' || screenSize === 'tablet'

  const getActionLayout = useCallback(
    (actions: React.ReactNode[]) => {
      if (!shouldCollapse) {
        return actions
      }

      // On mobile, show only primary action and collapse others
      if (actions.length <= 1) {
        return actions
      }

      // const [primary, ...secondary] = actions
      const [primary] = actions
      return [
        primary
        // Dropdown menu for secondary actions would go here
      ]
    },
    [shouldCollapse]
  )

  return {
    shouldCollapse,
    shouldGroup,
    getActionLayout
  }
}

// Hook for responsive font sizes
export const useResponsiveFontSize = (screenSize: ScreenSize) => {
  const getFontSizeClasses = useCallback(() => {
    switch (screenSize) {
      case 'mobile':
        return {
          header: 'text-xs',
          cell: 'text-xs',
          caption: 'text-xs'
        }
      case 'tablet':
        return {
          header: 'text-sm',
          cell: 'text-sm',
          caption: 'text-xs'
        }
      default:
        return {
          header: 'text-sm',
          cell: 'text-sm',
          caption: 'text-sm'
        }
    }
  }, [screenSize])

  return getFontSizeClasses()
}
